import { Add } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Avatar,
  Breadcrumbs,
  Button,
  Card,
  Link as LinkIcon,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Page } from '../../../design/page/Page';
import { EikenMaterialResource } from '../../../resources';
import { UserRole } from '../../../resources/BaseResource';
import { TokenCountResponse } from '../../../resources/EikenMaterialResource';
import { AdminCreditModal } from './components/AdminCreditModal';
import { CreditModal } from './components/CreditModal';
import { EditAIPromptsModal } from './components/EditAIPromptsModal';
import { GenerateMaterialConfimationModal } from './components/GenerateMaterialConfimationModal';

export function EikenMaterialChooseNewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [aiCount, setAiCount] = useState(1);
  const [manualCount, setManualCount] = useState(1);
  const [credits, setCredits] = useState<TokenCountResponse>({
    tokenCount: 0,
    purchasedTokenCount: 0,
  });
  const [isEditAIPromptsModalOpen, setIsEditAIPromptsModalOpen] = useState(false);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const testInitials =
    (user?.data.firstName?.charAt(0) || '') + (user?.data.lastName?.charAt(0) || '');
  const [isGenerateMaterialModalOpen, setIsGenerateMaterialModalOpen] = useState(false);

  const totalCredits = credits.tokenCount + credits.purchasedTokenCount;

  const isAdmin = user?.data.role === UserRole.Admin;

  useEffect(() => {
    EikenMaterialResource.getTokenCount().then((count) => {
      setCredits(count);
      const sum = count.tokenCount + count.purchasedTokenCount;
      setAiCount(Math.min(sum, aiCount));
    });
  }, []);

  return (
    <Page>
      <Page.Header sx={{ height: 'auto', pt: 4, pb: 2, mb: 2 }}>
        {/* Breadcrumbs and Title */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Link to="/eiken-materials">
            <Typography color="text.secondary" variant="body2">
              &lt; Back to questions
            </Typography>
          </Link>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Avatar
            sx={{ bgcolor: 'border.primary', width: 56, height: 56, fontSize: 24, fontWeight: 600 }}
          >
            {testInitials}
          </Avatar>
          <div>
            <Breadcrumbs aria-label="breadcrumb">
              <Link className="underline" color="inherit" to="/eiken-materials">
                Questions
              </Link>
              <Typography color="text.primary">Create Test</Typography>
            </Breadcrumbs>
            <Typography variant="h3" sx={{ fontWeight: 600 }}>
              Create Test
            </Typography>
          </div>
        </Stack>
      </Page.Header>
      <Page.Content>
        {/* Credits */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 4 }}>
          <Stack direction="column" alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {isAdmin
                ? 'Admin credit manager'
                : `${credits.tokenCount + credits.purchasedTokenCount} credits`}
            </Typography>
            <Typography variant="caption">
              {isAdmin ? 'Manage organization credit' : 'Remaining'}
            </Typography>
          </Stack>
          <Button
            sx={{ paddingX: 0, minWidth: 32 }}
            variant="outlined"
            size="small"
            color="primary"
            onClick={() => setIsCreditModalOpen(true)}
          >
            <AddIcon />
          </Button>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} justifyContent="center">
          {/* Generate with AI Card */}
          <Card
            sx={{
              flex: 1,
              minWidth: 320,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              px: 3,
            }}
          >
            <AutoAwesomeIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Generate with AI
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Generate test using AI{' '}
              {isAdmin && (
                <LinkIcon
                  underline="hover"
                  sx={{ fontWeight: 500, cursor: 'pointer' }}
                  onClick={() => setIsEditAIPromptsModalOpen(true)}
                >
                  Edit AI Prompts
                </LinkIcon>
              )}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
              <Button
                sx={{ paddingX: 0, minWidth: 36 }}
                variant="outlined"
                onClick={() => setAiCount(Math.max(1, aiCount - 1))}
                color="primary"
                disabled={totalCredits === 0}
              >
                <RemoveIcon />
              </Button>
              <Typography
                variant="h6"
                sx={{
                  minWidth: 36,
                  height: 36,
                  textAlign: 'center',
                  borderRadius: 2,
                  lineHeight: '32px',
                }}
                borderColor="border.1"
              >
                {aiCount}
              </Typography>
              <Button
                sx={{ paddingX: 0, minWidth: 36 }}
                variant="outlined"
                onClick={() => setAiCount(Math.min(totalCredits, aiCount + 1))}
                color="primary"
                disabled={totalCredits === 0}
              >
                <AddIcon />
              </Button>
              <Button
                onClick={() => setIsGenerateMaterialModalOpen(true)}
                variant="contained"
                color="primary"
                sx={{ ml: 2, px: 3, fontWeight: 600 }}
                disabled={aiCount > totalCredits || totalCredits === 0}
              >
                Generate tests
              </Button>
            </Stack>
          </Card>
          {/* Create Manually Card */}
          <Card
            sx={{
              flex: 1,
              minWidth: 320,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              px: 3,
            }}
          >
            <Add color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Create manually
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter material questions and information manually
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
              <Button
                sx={{ paddingX: 0, minWidth: 36 }}
                variant="outlined"
                onClick={() => setManualCount(Math.max(1, manualCount - 1))}
                color="primary"
                disabled={totalCredits === 0}
              >
                <RemoveIcon />
              </Button>

              <Typography
                variant="h6"
                sx={{
                  minWidth: 36,
                  height: 36,
                  textAlign: 'center',
                  borderRadius: 2,
                  lineHeight: '32px',
                }}
                borderColor="border.1"
              >
                {manualCount}
              </Typography>
              <Button
                sx={{ paddingX: 0, minWidth: 36 }}
                variant="outlined"
                onClick={() => setManualCount(manualCount + 1)}
                color="primary"
              >
                <AddIcon />
              </Button>
              <Button
                onClick={() => navigate('/eiken-materials/create-manual?count=' + manualCount)}
                variant="contained"
                color="primary"
                sx={{ ml: 2, px: 3, fontWeight: 600 }}
              >
                Create tests
              </Button>
            </Stack>
          </Card>
        </Stack>
        <EditAIPromptsModal
          key={isEditAIPromptsModalOpen ? 'open' : 'closed'}
          open={isEditAIPromptsModalOpen}
          onClose={() => setIsEditAIPromptsModalOpen(false)}
        />
        {isAdmin ? (
          <AdminCreditModal open={isCreditModalOpen} onClose={() => setIsCreditModalOpen(false)} />
        ) : (
          <CreditModal
            open={isCreditModalOpen}
            onClose={() => setIsCreditModalOpen(false)}
            credits={credits}
          />
        )}
        <GenerateMaterialConfimationModal
          open={isGenerateMaterialModalOpen}
          onClose={() => setIsGenerateMaterialModalOpen(false)}
          count={aiCount}
        />
      </Page.Content>
    </Page>
  );
}
